import API from "@core/api/api";

/**
 * Client Support Service
 * Services for CLIENT SUPPORT users to manage their assigned clients
 */

// ========================================
// CLIENT SUPPORT USER OPERATIONS
// ========================================

/**
 * Get all client support users (Admin/Manager only)
 */
export const getAllClientSupport = async () => {
  const response = await API.get("/users/client-support");
  return response.data;
};

/**
 * Get assigned clients for a support user
 */
export const getAssignedClients = async (supportId, params = {}) => {
  const queryParams = new URLSearchParams();

  if (params.page) queryParams.append("page", params.page);
  if (params.limit) queryParams.append("limit", params.limit);
  if (params.search) queryParams.append("search", params.search);
  if (params.isActive !== undefined)
    queryParams.append("isActive", params.isActive);

  const queryString = queryParams.toString();
  const url = `/users/client-support/${supportId}/clients${
    queryString ? `?${queryString}` : ""
  }`;

  const response = await API.get(url);
  return response.data;
};

/**
 * Get unassigned clients (Admin/Manager only)
 */
export const getUnassignedClients = async (params = {}) => {
  const queryParams = new URLSearchParams();

  if (params.page) queryParams.append("page", params.page);
  if (params.limit) queryParams.append("limit", params.limit);
  if (params.search) queryParams.append("search", params.search);

  const queryString = queryParams.toString();
  const url = `/users/unassigned-clients${
    queryString ? `?${queryString}` : ""
  }`;

  const response = await API.get(url);
  return response.data;
};

/**
 * Assign clients to a support user (Admin/Manager only)
 */
export const assignClients = async (supportId, clientIds) => {
  const response = await API.post(`/users/client-support/${supportId}/assign`, {
    clientIds,
  });
  return response.data;
};

/**
 * Unassign clients from a support user (Admin/Manager only)
 */
export const unassignClients = async (supportId, clientIds) => {
  const response = await API.post(
    `/users/client-support/${supportId}/unassign`,
    {
      clientIds,
    }
  );
  return response.data;
};

/**
 * Get support user details
 */
export const getSupportUserDetails = async (supportId) => {
  const response = await API.get(`/users/${supportId}`);
  return response.data;
};

// ========================================
// DASHBOARD STATISTICS
// ========================================

/**
 * Get dashboard statistics for CLIENT SUPPORT user
 * This fetches packages, tickets, and invoices for assigned clients
 */
export const getClientSupportDashboardStats = async (supportId) => {
  const response = await API.get(
    `/users/client-support/${supportId}/dashboard-stats`
  );
  return response.data;
};

/**
 * Get packages for assigned clients
 */
export const getAssignedClientsPackages = async (params = {}) => {
  const queryParams = new URLSearchParams();

  if (params.page) queryParams.append("page", params.page);
  if (params.limit) queryParams.append("limit", params.limit);
  if (params.status) queryParams.append("status", params.status);
  if (params.search) queryParams.append("search", params.search);
  if (params.from) queryParams.append("from", params.from);
  if (params.to) queryParams.append("to", params.to);

  const queryString = queryParams.toString();
  const url = `/packages${queryString ? `?${queryString}` : ""}`;

  const response = await API.get(url);
  return response.data;
};

/**
 * Get tickets for assigned clients
 */
export const getAssignedClientsTickets = async (params = {}) => {
  const queryParams = new URLSearchParams();

  if (params.page) queryParams.append("page", params.page);
  if (params.limit) queryParams.append("limit", params.limit);
  if (params.status) queryParams.append("status", params.status);
  if (params.priority) queryParams.append("priority", params.priority);

  const queryString = queryParams.toString();
  const url = `/tickets${queryString ? `?${queryString}` : ""}`;

  const response = await API.get(url);
  return response.data;
};

/**
 * Get invoices for assigned clients
 */
export const getAssignedClientsInvoices = async (params = {}) => {
  const queryParams = new URLSearchParams();

  if (params.page) queryParams.append("page", params.page);
  if (params.limit) queryParams.append("limit", params.limit);
  if (params.status) queryParams.append("status", params.status);

  const queryString = queryParams.toString();
  const url = `/invoices${queryString ? `?${queryString}` : ""}`;

  const response = await API.get(url);
  return response.data;
};

/**
 * Get pickups for assigned clients
 */
export const getAssignedClientsPickups = async (params = {}) => {
  const queryParams = new URLSearchParams();

  if (params.page) queryParams.append("page", params.page);
  if (params.limit) queryParams.append("limit", params.limit);
  if (params.status) queryParams.append("status", params.status);

  const queryString = queryParams.toString();
  const url = `/pickups${queryString ? `?${queryString}` : ""}`;

  const response = await API.get(url);
  return response.data;
};

/**
 * Get return vouchers for assigned clients
 */
export const getAssignedClientsReturnVouchers = async (params = {}) => {
  const queryParams = new URLSearchParams();

  if (params.page) queryParams.append("page", params.page);
  if (params.limit) queryParams.append("limit", params.limit);
  if (params.status) queryParams.append("status", params.status);

  const queryString = queryParams.toString();
  const url = `/return-vouchers${queryString ? `?${queryString}` : ""}`;

  const response = await API.get(url);
  return response.data;
};

/**
 * Get products for assigned clients
 */
export const getAssignedClientsProducts = async (params = {}) => {
  const queryParams = new URLSearchParams();

  if (params.page) queryParams.append("page", params.page);
  if (params.limit) queryParams.append("limit", params.limit);
  if (params.search) queryParams.append("search", params.search);
  if (params.category) queryParams.append("category", params.category);
  if (params.lowStock !== undefined)
    queryParams.append("lowStock", params.lowStock);

  const queryString = queryParams.toString();
  const url = `/products${queryString ? `?${queryString}` : ""}`;

  const response = await API.get(url);
  return response.data;
};

// ========================================
// CLIENT OPERATIONS
// ========================================

/**
 * Get specific client details (for assigned clients only)
 */
export const getClientDetails = async (clientId) => {
  const response = await API.get(`/users/${clientId}`);
  return response.data;
};

/**
 * Get client's recent activity
 */
export const getClientActivity = async (clientId, params = {}) => {
  const queryParams = new URLSearchParams();

  if (params.limit) queryParams.append("limit", params.limit);
  if (params.type) queryParams.append("type", params.type); // packages, tickets, invoices

  const queryString = queryParams.toString();
  const url = `/users/${clientId}/activity${
    queryString ? `?${queryString}` : ""
  }`;

  const response = await API.get(url);
  return response.data;
};
